import {
  browserLocks,
  idbKv,
  safeLocalStorage,
  type ExclusiveLocks,
  type Kv,
} from './browser';
import { isSessionQueryKey, type StoredClient } from './cache-account';
import { accountRef, type AccountRef } from './account';

/**
 * Whose draft it is (MAP 225a, ruling R4: a draft shows only to the person who wrote it, and
 * waits for them).
 *
 * A visit note used to be saved under `draft:visit-note:<visitId>`, which belongs to nobody:
 * on a shared tablet the next person to open that stop was shown what the last one typed.
 * The key now carries the writer.
 */
export type DraftKind =
  | 'visit-note'
  /** "Flag a problem" on a visit (ticket 03): its title, what's wrong, and the minted id. */
  | 'ticket-flag'
  /** "Add a message" on a ticket the tech can see (ticket 03). */
  | 'ticket-message';

export function draftKey(kind: DraftKind, userId: string, id: string): string {
  return `draft:${kind}:${userId}:${id}`;
}

/** Is this a draft key of this kind (as `draftKey` makes them)? */
export function isDraftKey(kind: DraftKind, key: unknown): key is string {
  return typeof key === 'string' && key.startsWith(`draft:${kind}:`);
}

/** A visit note saved before drafts had a writer: exactly one segment after the kind. */
const LEGACY_VISIT_NOTE = /^draft:visit-note:([^:]+)$/;

/**
 * The migration's own state in localStorage: done or not, and WHO the drafts go to, fixed at
 * the first read. A later start reads a blob this release saved, which has no `me.session`;
 * without the user written down here, a migration that died halfway would delete the drafts
 * it had not yet moved instead of moving them.
 */
export const DRAFT_MIGRATION_STATE = 'servwell-draft-migration';
export const DRAFT_MIGRATION_LOCK = 'servwell-draft-migration';
/** How long a start waits for another tab's migration before leaving it for next time. */
const LOCK_WAIT_MS = 5_000;

type MigrationState = { version: 1; done: boolean; userId: string | null };

export interface DraftMigrationEnv {
  kv: Kv;
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  locks: ExclusiveLocks | undefined;
  /** The give-up signal for the lock wait. */
  lockSignal?: () => AbortSignal;
}

/**
 * Who a blob saved BEFORE MAP 225a was signed in as: its persisted `me.session`. Only such a
 * blob has one, which is why the persister hands the raw blob over before the restore strips
 * it. The shop is null when that session had none.
 */
function legacySession(stored: unknown): { userId: string; orgId: string | null } | null {
  const queries = (stored as StoredClient | undefined)?.clientState?.queries;
  if (!Array.isArray(queries)) return null;
  for (const q of queries) {
    if (!isSessionQueryKey(q.queryKey)) continue;
    const data = q.state?.data as
      | { authenticated?: unknown; userId?: unknown; orgId?: unknown }
      | undefined;
    if (data?.authenticated === true && typeof data.userId === 'string') {
      return {
        userId: data.userId,
        orgId: typeof data.orgId === 'string' && data.orgId ? data.orgId : null,
      };
    }
  }
  return null;
}

/** The user a pre-225a blob was signed in as: whose its drafts are. */
export function legacySessionUserId(stored: unknown): string | null {
  return legacySession(stored)?.userId ?? null;
}

/**
 * The person AND shop a pre-225a blob was signed in as: whose its queued writes are (MAP
 * 225b, legacy-writes.ts). Null without a shop: a write belongs to a person in a shop.
 */
export function legacySessionAccount(stored: unknown): AccountRef | null {
  const session = legacySession(stored);
  return session?.orgId
    ? accountRef({ userId: session.userId, orgId: session.orgId })
    : null;
}

function readState(env: DraftMigrationEnv): MigrationState | null {
  try {
    const raw = env.storage?.getItem(DRAFT_MIGRATION_STATE);
    const state = raw ? (JSON.parse(raw) as MigrationState) : null;
    return state?.version === 1 ? state : null;
  } catch {
    return null;
  }
}

function writeState(env: DraftMigrationEnv, state: MigrationState): void {
  try {
    env.storage?.setItem(DRAFT_MIGRATION_STATE, JSON.stringify(state));
  } catch {
    // Private mode or a full disk: the migration runs again next time, and every step of it
    // is safe to repeat.
  }
}

/**
 * Move every legacy visit note to the user the device was signed in as when it was written,
 * or delete it when nobody is known. Once.
 *
 * It runs inside the persister's restore, BEFORE the blob is hydrated, because the only record
 * of who that was is the old blob's persisted `me.session`, which the restore then strips.
 *
 * - **Once:** `done` in the migration state.
 * - **One tab at a time** where there is `navigator.locks`. A start that waits too long for
 *   another tab's lock moves and deletes nothing, and leaves it for next time. Without locks
 *   it runs unlocked, which is safe because every step is: per key, copy only if the new key
 *   is ABSENT (one transaction), then delete.
 *
 * No claim screen is built: losing a draft nobody can be named for beats showing it to the
 * wrong person (Codex r1, Important 7).
 */
export async function migrateLegacyDrafts(
  stored: unknown,
  env: DraftMigrationEnv,
): Promise<void> {
  const state = readState(env);
  if (state?.done) return;
  const userId = state ? state.userId : legacySessionUserId(stored);
  if (!state) writeState(env, { version: 1, done: false, userId });
  const move = () => moveDrafts(userId, env);
  if (!env.locks) return move();
  const signal = env.lockSignal?.() ?? AbortSignal.timeout(LOCK_WAIT_MS);
  try {
    await env.locks.request(DRAFT_MIGRATION_LOCK, { signal }, move);
  } catch (err) {
    if (!signal.aborted) throw err;
    console.warn('[servwell] another tab is moving old drafts; left for next time');
  }
}

async function moveDrafts(userId: string | null, env: DraftMigrationEnv): Promise<void> {
  for (const key of await env.kv.keys()) {
    const visitId =
      typeof key === 'string' ? LEGACY_VISIT_NOTE.exec(key)?.[1] : undefined;
    if (visitId === undefined) continue;
    const text = await env.kv.get(key);
    if (userId !== null && text !== undefined) {
      // Copy only if absent, in ONE readwrite transaction: a second tab running this at the
      // same moment (no Web Locks in that browser) reads the target inside the same
      // transaction it writes in, so it can never put the old text over a draft the tech has
      // edited since the first tab moved it (Codex r2).
      await env.kv.update(
        draftKey('visit-note', userId, visitId),
        (current) => current ?? text,
      );
    }
    await env.kv.del(key);
  }
  writeState(env, { version: 1, done: true, userId });
}

/** The real thing: the default idb-keyval store (where drafts live), localStorage, locks. */
export function migrateLegacyDraftsOnThisDevice(stored: unknown): Promise<void> {
  return migrateLegacyDrafts(stored, {
    kv: idbKv,
    storage: safeLocalStorage(),
    locks: browserLocks(),
  });
}
